import React, { useEffect, useMemo, useState, useCallback, useRef, memo } from 'react';
import { toast } from 'sonner';
import { GripVertical, ArrowUpToLine, ArrowDownToLine } from 'lucide-react';
import { DndContext, closestCenter } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  useSortable,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'components/ui/accordion';
import { ToggleGroup, ToggleGroupItem } from 'components/ui/toggle-group';
import { Checkbox } from 'components/ui/checkbox';
import { Input } from 'components/ui/input';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Alert, AlertDescription, AlertTitle } from 'components/ui/alert';
import { Popover, PopoverAnchor, PopoverContent } from 'components/ui/popover';
import LoadingState from 'components/shared/LoadingState';
import {
  getPlayerStatsLabels,
  getTeamStatsLabels,
  setPlayerStatsLabels,
  setTeamStatsLabels,
  getAvailabilityStatsLabels,
  setAvailabilityStatsLabels,
} from '../../services/api';
import SaveBar, { useSaveShortcut, useUnsavedWarning } from './SaveBar';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
const toTitleCase = (str = '') =>
  str.replace(/_/g, ' ').replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.slice(1));

// ─────────────────────────────────────────────────────────────────────────────
// Category combobox — free-text input + suggestion popover (replaces the MUI
// freeSolo Autocomplete). Commits on Enter, blur, or option click.
// ─────────────────────────────────────────────────────────────────────────────
const CategoryCombobox = memo(({ value, categories, onCommit }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(value || 'Other');

  // Keep local text in sync when the committed value changes externally
  useEffect(() => {
    setText(value || 'Other');
  }, [value]);

  const filtered = useMemo(() => {
    const q = (text || '').toLowerCase();
    if (!q) return categories;
    return categories.filter((c) => c.toLowerCase().includes(q));
  }, [categories, text]);

  const commit = (nextValue) => {
    const v = ((nextValue ?? text) || '').trim();
    if (v && v !== value) onCommit(v);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <Input
          className="h-8 text-token-sm"
          placeholder="Category"
          value={text}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          onChange={(e) => {
            setText(e.target.value);
            setOpen(true);
          }}
          onBlur={() => {
            setOpen(false);
            commit();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              commit();
              setOpen(false);
            }
            if (e.key === 'Escape') setOpen(false);
          }}
        />
      </PopoverAnchor>
      <PopoverContent
        align="start"
        className="max-h-[260px] w-[200px] overflow-y-auto p-1"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {filtered.length === 0 ? (
          <p className="px-2 py-1.5 text-token-sm text-muted-foreground">
            Press Enter to add &ldquo;{text}&rdquo;
          </p>
        ) : (
          filtered.map((cat) => (
            <button
              key={cat}
              type="button"
              className="block w-full rounded-sm px-2 py-1.5 text-left text-token-sm hover:bg-muted"
              // preventDefault keeps the input focused so blur doesn't fire first
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                setText(cat);
                commit(cat);
                setOpen(false);
              }}
            >
              {cat}
            </button>
          ))
        )}
      </PopoverContent>
    </Popover>
  );
});

CategoryCombobox.displayName = 'CategoryCombobox';

// ─────────────────────────────────────────────────────────────────────────────
// Memoized Row Component - This prevents unnecessary re-renders
// ─────────────────────────────────────────────────────────────────────────────
const StatRow = memo(
  ({ row, idx, onUpdate, categories, isPlayerStats, isAvailability }) => {
    const handleTextChange = useCallback(
      (e) => {
        onUpdate(idx, 'stat_nicename', e.target.value);
      },
      [idx, onUpdate]
    );

    const handleFlagChange = useCallback(
      (field) => (checked) => {
        onUpdate(idx, field, checked ? 1 : 0);
      },
      [idx, onUpdate]
    );

    const handleCategoryCommit = useCallback(
      (newValue) => {
        onUpdate(idx, 'action_category1', newValue || 'Other');
      },
      [idx, onUpdate]
    );

    // Don't show category picker for FPL stats (they're always grouped as "FPL Stats")
    const isFplStat = row.type === 'fpl stat';

    // Simplified row for availability stats — nicename editing only
    if (isAvailability) {
      return (
        <div className="flex items-center gap-4 border-t border-borderGrey px-4 py-2 transition-colors hover:bg-neutral-50">
          <div className="min-w-[200px] flex-1">
            <p className="mb-1 text-token-sm text-muted-foreground">{row.field}</p>
            <Input
              className="h-8 text-token-base"
              placeholder="Stat nicename"
              value={row.stat_nicename || ''}
              onChange={handleTextChange}
            />
          </div>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-4 border-t border-borderGrey px-4 py-2 transition-colors hover:bg-neutral-50">
        {/* Field name and text input stacked */}
        <div className="min-w-[180px] flex-1">
          <p className="mb-1 text-token-sm text-muted-foreground">{row.field}</p>
          <Input
            className="h-8 text-token-base"
            placeholder="Stat nicename"
            value={row.stat_nicename || ''}
            onChange={handleTextChange}
          />
        </div>

        {/* Category picker - only for non-FPL stats */}
        <div className="w-[160px] shrink-0">
          {isFplStat ? (
            <Badge variant="outline" className="border-borderGrey bg-muted text-muted-foreground">
              FPL
            </Badge>
          ) : (
            <CategoryCombobox
              value={row.action_category1 || 'Other'}
              categories={categories}
              onCommit={handleCategoryCommit}
            />
          )}
        </div>

        {/* Checkboxes - compact with labels */}
        <div className="flex shrink-0 items-center gap-3">
          {isPlayerStats && (
            <label className="flex cursor-pointer items-center gap-1.5">
              <Checkbox
                checked={Boolean(row.use_in_playercard)}
                onCheckedChange={handleFlagChange('use_in_playercard')}
              />
              <span className="text-token-sm text-muted-foreground">PC</span>
            </label>
          )}
          <label className="flex cursor-pointer items-center gap-1.5">
            <Checkbox
              checked={Boolean(row.use_in_statsroom)}
              onCheckedChange={handleFlagChange('use_in_statsroom')}
            />
            <span className="text-token-sm text-muted-foreground">SR</span>
          </label>
          <label className="flex cursor-pointer items-center gap-1.5">
            <Checkbox
              checked={Boolean(row.use_in_modelling)}
              onCheckedChange={handleFlagChange('use_in_modelling')}
            />
            <span className="text-token-sm text-muted-foreground">MOD</span>
          </label>
        </div>
      </div>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison: only re-render if relevant fields actually changed
    // This prevents re-renders when parent state changes but this row's data didn't
    return (
      prevProps.row.stat_nicename === nextProps.row.stat_nicename &&
      prevProps.row.action_category1 === nextProps.row.action_category1 &&
      prevProps.row.use_in_playercard === nextProps.row.use_in_playercard &&
      prevProps.row.use_in_statsroom === nextProps.row.use_in_statsroom &&
      prevProps.row.use_in_modelling === nextProps.row.use_in_modelling &&
      prevProps.idx === nextProps.idx &&
      prevProps.onUpdate === nextProps.onUpdate &&
      prevProps.isPlayerStats === nextProps.isPlayerStats &&
      prevProps.isAvailability === nextProps.isAvailability
      // Note: categories array doesn't need deep comparison since we optimized its memoization
    );
  }
);

StatRow.displayName = 'StatRow';

// ─────────────────────────────────────────────────────────────────────────────
// Sortable Data Centre Row — dnd-kit stays; drag via the grip handle so the
// move-to-top/bottom buttons stay clickable.
// ─────────────────────────────────────────────────────────────────────────────
const SortableStatItem = memo(({ label, index, total, onMoveToTop, onMoveToBottom }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: label.field,
  });

  const style = {
    transform: CSS.Transform.toString(transform ? { ...transform, x: 0 } : transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 border-t border-borderGrey bg-white px-3 py-2 transition-colors first:border-t-0 hover:bg-neutral-50"
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="cursor-grab touch-none rounded-sm p-1 text-neutral-400 hover:text-foreground"
        aria-label={`Drag to reorder ${label.stat_nicename || label.field}`}
      >
        <GripVertical className="h-4 w-4" aria-hidden="true" />
      </button>
      <span className="w-8 shrink-0 text-token-base text-muted-foreground">{index + 1}</span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-token-base font-semibold">
          {label.stat_nicename || label.field}
        </p>
        <p className="truncate text-token-sm text-muted-foreground">{label.field}</p>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          disabled={index === 0}
          onClick={() => onMoveToTop(label.field)}
          title="Move to top"
          aria-label="Move to top"
        >
          <ArrowUpToLine className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          disabled={index === total - 1}
          onClick={() => onMoveToBottom(label.field)}
          title="Move to bottom"
          aria-label="Move to bottom"
        >
          <ArrowDownToLine className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
});

SortableStatItem.displayName = 'SortableStatItem';

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────
export default function StatsLabels() {
  const [statsLabels, setStatsLabels] = useState([]);
  const [statsType, setStatsType] = useState('player');
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Data Centre mode state. dcEntity picks whose column order is edited:
  // 'player' (player_stats_labels, the Players view) or 'team'
  // (team_stats_labels, the Teams view). Both use data_centre_order.
  const [fullLabels, setFullLabels] = useState([]);
  const [dataCentreLabels, setDataCentreLabels] = useState([]);
  const [dcEntity, setDcEntity] = useState('player');
  // Which entity the loaded Data Centre rows belong to, and a request counter so a slow earlier fetch can never
  // land after a later one (saving player rows through setTeamStatsLabels would REPLACE the team table).
  const [loadedEntity, setLoadedEntity] = useState(null);
  const fetchSeq = useRef(0);

  const isDataCentre = statsType === 'dataCentre';
  const isTeamOrder = isDataCentre && dcEntity === 'team';

  // ─── Fetch labels ──────────────────────────────────────────────────────────
  const fetchLabels = useCallback(async (type, entity = 'player') => {
    const seq = ++fetchSeq.current;
    const stale = () => seq !== fetchSeq.current;
    setLoading(true);
    setLoadError(false);
    setLoadedEntity(null);
    try {
      let result;
      if (type === 'dataCentre') {
        result = entity === 'team' ? await getTeamStatsLabels() : await getPlayerStatsLabels();
        if (stale()) return;
        const allLabels = result?.statsLabels || [];
        setFullLabels(allLabels);
        setLoadedEntity(entity);
        // 0 / null = not ordered yet: keep them after the ordered ones, by field
        const rank = (l) => (Number(l.data_centre_order) > 0 ? Number(l.data_centre_order) : Infinity);
        const srLabels = allLabels
          .filter((l) => Number(l.use_in_statsroom) === 1 || l.use_in_statsroom === true)
          .sort((a, b) => rank(a) - rank(b) || String(a.field).localeCompare(String(b.field)));
        setDataCentreLabels(srLabels);
        setStatsLabels([]);
      } else if (type === 'player') {
        result = await getPlayerStatsLabels();
        if (stale()) return;
        setStatsLabels(result?.statsLabels || []);
      } else if (type === 'team') {
        result = await getTeamStatsLabels();
        if (stale()) return;
        setStatsLabels(result?.statsLabels || []);
      } else {
        result = await getAvailabilityStatsLabels();
        if (stale()) return;
        setStatsLabels(result?.statsLabels || []);
      }
      setDirty(false);
    } catch (err) {
      if (stale()) return;
      console.error(`Error fetching ${type} stats labels:`, err);
      setLoadError(true);
    } finally {
      if (!stale()) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLabels(statsType, dcEntity);
  }, [statsType, dcEntity, fetchLabels]);

  useUnsavedWarning(dirty);

  // ─── State helpers ─────────────────────────────────────────────────────────
  const handleStatsTypeChange = (newType) => {
    if (newType) setStatsType(newType);
  };

  // Memoized update function to prevent recreating on each render
  const updateRow = useCallback((rowIdx, key, value) => {
    setDirty(true);
    setStatsLabels((prev) => {
      const newLabels = [...prev];
      newLabels[rowIdx] = { ...newLabels[rowIdx], [key]: value };
      return newLabels;
    });
  }, []);

  // ─── Data Centre reorder handlers ──────────────────────────────────────────
  const onDragEnd = useCallback(({ active, over }) => {
    if (!over || active.id === over.id) return;
    setDirty(true);
    setDataCentreLabels((prev) => {
      const oldIndex = prev.findIndex((l) => l.field === active.id);
      const newIndex = prev.findIndex((l) => l.field === over.id);
      return arrayMove(prev, oldIndex, newIndex).map((l, i) => ({ ...l, data_centre_order: i + 1 }));
    });
  }, []);

  const moveTo = useCallback((field, position) => {
    setDataCentreLabels((prev) => {
      const from = prev.findIndex((l) => l.field === field);
      if (from === -1) return prev;
      const to = position === 'top' ? 0 : prev.length - 1;
      if (from === to) return prev;
      setDirty(true);
      return arrayMove(prev, from, to).map((l, i) => ({ ...l, data_centre_order: i + 1 }));
    });
  }, []);

  const handleMoveToTop = useCallback((field) => moveTo(field, 'top'), [moveTo]);
  const handleMoveToBottom = useCallback((field) => moveTo(field, 'bottom'), [moveTo]);

  // ─── Save labels ───────────────────────────────────────────────────────────
  const handleSubmit = useCallback(async () => {
    if (saving) return;
    setSaving(true);
    try {
      if (isDataCentre) {
        if (loadedEntity !== dcEntity) {
          toast.error('Labels are still loading - try again in a moment');
          return;
        }
        // Merge updated orders back into the full labels array
        const orderMap = new Map(dataCentreLabels.map((l) => [l.field, l.data_centre_order]));
        const updatedFull = fullLabels.map((l) => ({
          ...l,
          data_centre_order:
            orderMap.get(l.field) ?? l.data_centre_order ?? (isTeamOrder ? 0 : null),
        }));
        if (isTeamOrder) await setTeamStatsLabels({ statsLabels: updatedFull });
        else await setPlayerStatsLabels({ statsLabels: updatedFull });
      } else if (statsType === 'player') {
        await setPlayerStatsLabels({ statsLabels });
      } else if (statsType === 'team') {
        await setTeamStatsLabels({ statsLabels });
      } else {
        await setAvailabilityStatsLabels({ statsLabels });
      }
      setDirty(false);
      toast.success(isDataCentre ? 'Column order saved' : 'Stats labels saved');
    } catch (err) {
      console.error('Error saving stats labels', err);
      toast.error(isDataCentre ? 'Failed to save column order' : 'Failed to save stats labels');
    } finally {
      setSaving(false);
    }
  }, [saving, isDataCentre, isTeamOrder, dcEntity, loadedEntity, statsType, statsLabels, fullLabels, dataCentreLabels]);

  // ─── Extract unique categories for autocomplete ───────────────────────────
  // Only recalculate when the actual category values change, not on every keystroke
  const categories = useMemo(() => {
    const cats = new Set();
    statsLabels.forEach((row) => {
      if (row.action_category1 && row.type !== 'fpl stat') {
        cats.add(row.action_category1);
      }
    });
    // Add some default categories if not present
    ['Attacking', 'Defensive', 'Passing', 'Possession', 'Set Pieces', 'Other'].forEach((c) =>
      cats.add(c)
    );
    return Array.from(cats).sort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // Only depend on the category values, not the entire statsLabels array
    // eslint-disable-next-line react-hooks/exhaustive-deps
    statsLabels.map((row) => row.action_category1).join(','),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    statsLabels.map((row) => row.type).join(','),
  ]);

  // ─── Group rows for accordion display ─────────────────────────────────────
  // Store only indices in groups (stable), rows will be looked up when rendering
  const groupedIndices = useMemo(() => {
    if (isDataCentre) return {};
    const groups = {};
    statsLabels.forEach((row, idx) => {
      let groupName;
      if (statsType === 'availability') {
        // Availability stats use 'category' field
        groupName = toTitleCase(row.category || 'Other');
      } else {
        groupName =
          row.type === 'fpl stat' ? 'FPL Stats' : toTitleCase(row.action_category1 || 'Other');
      }
      if (!groups[groupName]) groups[groupName] = [];
      groups[groupName].push(idx);
    });
    return groups;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    statsType,
    isDataCentre,
    // Only depend on fields used for grouping
    // eslint-disable-next-line react-hooks/exhaustive-deps
    statsLabels.map((row) => row.category).join(','),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    statsLabels.map((row) => row.action_category1).join(','),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    statsLabels.map((row) => row.type).join(','),
    statsLabels.length, // Recalculate if rows are added/removed
  ]);

  const hasData = isDataCentre ? dataCentreLabels.length > 0 : statsLabels.length > 0;
  const canSave = !loading && !loadError && hasData && (!isDataCentre || loadedEntity === dcEntity);
  useSaveShortcut(handleSubmit, canSave);

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="pb-24">
      {/* Toggle and Legend */}
      <div className="mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <ToggleGroup type="single" value={statsType} onValueChange={handleStatsTypeChange}>
            <ToggleGroupItem value="player">Player Stats</ToggleGroupItem>
            <ToggleGroupItem value="team">Team Stats</ToggleGroupItem>
            <ToggleGroupItem value="availability">Availability</ToggleGroupItem>
            <ToggleGroupItem value="dataCentre">Data Centre</ToggleGroupItem>
          </ToggleGroup>
          {isDataCentre && (
            <ToggleGroup
              type="single"
              size="sm"
              value={dcEntity}
              onValueChange={(v) => v && setDcEntity(v)}
              aria-label="Data Centre view"
            >
              <ToggleGroupItem value="player">Players</ToggleGroupItem>
              <ToggleGroupItem value="team">Teams</ToggleGroupItem>
            </ToggleGroup>
          )}
          {dirty && <Badge variant="warning">Unsaved changes</Badge>}
        </div>

        {/* Team model caveat: the live GLM backend uses a hardcoded feature
            list (Elo + xG EWMAs), so team MOD toggles only affect the model
            when TEAM_MODEL_BACKEND=hgbr. Rolling features are still computed
            for enabled stats either way. */}
        {statsType === 'team' && (
          <Alert className="mt-3 max-w-3xl">
            <AlertTitle>Team MOD toggles are currently inert</AlertTitle>
            <AlertDescription>
              The live team model (GLM backend) uses a fixed Elo + xG feature set and ignores this
              selection. Toggles here only take effect if the pipeline runs with
              TEAM_MODEL_BACKEND=hgbr.
            </AlertDescription>
          </Alert>
        )}

        {/* Legend */}
        <div className="mt-3 rounded-token-medium bg-muted px-4 py-3">
          <p className="text-token-sm text-muted-foreground">
            {isDataCentre ? (
              <>
                <strong className="text-textSecondary">Data Centre:</strong> Drag to reorder the
                columns shown in the Data Centre {isTeamOrder ? 'Teams' : 'Players'} view.
                <br />
                Only stats with Statsroom (SR) enabled are shown. Toggle SR and set nicenames on the{' '}
                {isTeamOrder ? 'Team Stats' : 'Player Stats'} tab.
              </>
            ) : statsType === 'availability' ? (
              <>
                <strong className="text-textSecondary">Availability Stats:</strong> Edit nicenames
                for availability features.
                <br />
                Modelling feature selection is configured on the Modelling Features page.
              </>
            ) : (
              <>
                <strong className="text-textSecondary">Legend:</strong>{' '}
                {statsType === 'player' ? 'PC = Player Card, ' : ''}SR = Statsroom, MOD = Modelling
                <br />
                <strong className="text-textSecondary">Category:</strong> Type or select a category
                to group stats in the UI
              </>
            )}
          </p>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <LoadingState message="Loading stats labels..." />
      ) : loadError ? (
        <Alert variant="destructive">
          <AlertTitle>Failed to load stats labels</AlertTitle>
          <AlertDescription className="flex flex-wrap items-center gap-3">
            <span>Something went wrong while fetching the data.</span>
            <Button variant="outline" size="sm" onClick={() => fetchLabels(statsType, dcEntity)}>
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      ) : !hasData ? (
        <p className="p-6 text-center text-token-md text-muted-foreground">No stats found.</p>
      ) : isDataCentre ? (
        /* ─── Data Centre: drag-to-reorder list ─── */
        <div className="overflow-hidden rounded-token-large border border-borderGrey bg-white">
          <p className="border-b border-borderGrey px-4 py-3 text-token-md font-semibold">
            {isTeamOrder ? 'Teams' : 'Players'} column order ({dataCentreLabels.length} stats)
          </p>
          <DndContext
            modifiers={[restrictToVerticalAxis]}
            onDragEnd={onDragEnd}
            collisionDetection={closestCenter}
          >
            <SortableContext
              items={dataCentreLabels.map((l) => l.field)}
              strategy={verticalListSortingStrategy}
            >
              <div className="flex flex-col">
                {dataCentreLabels.map((label, idx) => (
                  <SortableStatItem
                    key={label.field}
                    label={label}
                    index={idx}
                    total={dataCentreLabels.length}
                    onMoveToTop={handleMoveToTop}
                    onMoveToBottom={handleMoveToBottom}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      ) : (
        /* ─── Standard accordion view ─── */
        <Accordion
          key={statsType}
          type="multiple"
          defaultValue={[statsType === 'availability' ? '10g Window' : 'FPL Stats']}
          className="space-y-2"
        >
          {Object.entries(groupedIndices).map(([groupName, indices]) => (
            <AccordionItem
              key={groupName}
              value={groupName}
              className="overflow-hidden rounded-token-large border border-borderGrey bg-white"
            >
              <AccordionTrigger className="px-4 py-3 text-token-md font-semibold hover:no-underline">
                {groupName} ({indices.length})
              </AccordionTrigger>
              <AccordionContent className="p-0">
                {indices.map((idx) => (
                  <StatRow
                    key={`${statsType}-${statsLabels[idx].field}`}
                    row={statsLabels[idx]}
                    idx={idx}
                    onUpdate={updateRow}
                    categories={categories}
                    isPlayerStats={statsType === 'player'}
                    isAvailability={statsType === 'availability'}
                  />
                ))}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}

      {/* Submit Button */}
      {canSave && (
        <SaveBar
          onSave={handleSubmit}
          saving={saving}
          label={isDataCentre ? 'Save Order' : 'Submit Labels'}
        />
      )}
    </div>
  );
}
